<template>
  <div id="app" :class="{ 'has-shell': showShell, 'side-collapsed': sidebarCollapsed }">

    <!-- Authenticated app shell: sidebar + topbar + content -->
    <template v-if="showShell">
      <OmxSidebar
          :logged-in="isLoggedIn"
          :username="username"
          :role="userRoleLabel"
          :collapsed="sidebarCollapsed"
          :open-mobile="mobileOpen"
          @toggle="sidebarCollapsed = !sidebarCollapsed"
          @close-mobile="mobileOpen = false"
          @endpoint-changed="onEndpointChanged"
          @account="showUserAccountClicked"
          @logout="logout"
      />
      <div class="omx-shell__main">
        <OmxTopbar
            :logged-in="isLoggedIn"
            @open-mobile="mobileOpen = true"
            @open-command="commandOpen = true"
            @account="showUserAccountClicked"
            @logout="logout"
        />
        <main class="omx-shell__content">
          <router-view :key="$route.path" />
        </main>
      </div>

      <OmxCommandPalette
          :open="commandOpen"
          :logged-in="isLoggedIn"
          @close="commandOpen = false"
          @account="showUserAccountClicked"
          @logout="logout"
          @endpoint-changed="onEndpointChanged"
      />
    </template>

    <!-- Public routes: full-screen, no shell -->
    <template v-else>
      <router-view :key="$route.path" />
    </template>
  </div>
</template>

<script>
import firebase from "firebase/app";
import OmxSidebar from "@/components/shell/OmxSidebar.vue";
import OmxTopbar from "@/components/shell/OmxTopbar.vue";
import OmxCommandPalette from "@/components/shell/OmxCommandPalette.vue";
import { ls } from "@/utils/storage";

const PUBLIC_ROUTES = new Set(['Login', 'SignUp', 'PasswordReset']);

export default {
  name: 'App',
  components: { OmxSidebar, OmxTopbar, OmxCommandPalette },
  data() {
    return {
      userRole: {},
      isLoggedIn: false,
      username: null,
      sidebarCollapsed: this.readSidebarPref(),
      mobileOpen: false,
      commandOpen: false
    };
  },
  computed: {
    showShell() {
      // Shell appears for any non-public route once login state is known.
      // While unauthenticated (or on login/signup/reset), routes render full-screen.
      if (!this.$route || !this.$route.name) return false;
      if (PUBLIC_ROUTES.has(this.$route.name)) return false;
      return this.isLoggedIn;
    },
    userRoleLabel() {
      return this.getLocalUserRole();
    }
  },
  watch: {
    sidebarCollapsed(val) {
      ls.set('omx-sidebar-collapsed', val ? '1' : '0');
    },
    $route() {
      // route changes auto-close the mobile drawer & command palette
      this.mobileOpen = false;
      this.commandOpen = false;
    }
  },
  methods: {
    readSidebarPref() {
      return ls.get('omx-sidebar-collapsed') === '1';
    },
    onCommandShortcut(e) {
      const isMeta = e.metaKey || e.ctrlKey;
      if (isMeta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        if (this.showShell) {
          this.commandOpen = true;
        }
      }
    },
    onEndpointChanged() {
      // Reload so all in-flight closures pick the new endpoint cleanly
      this.$buefy.toast.open({
        message: this.$t('shell.endpoint-changed'),
        type: 'is-success',
        duration: 1800
      });
      setTimeout(() => window.location.reload(), 300);
    },
    logout() {
      firebase.auth().signOut().then(() => {
        ls.remove("currentUser");
        this.removeUserRole();
        this.$router.replace("/").catch(() => { /* navigation duplicated */ });
      });
    },
    showUserAccountClicked() {
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getUserFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getUserFunction({ action: "get_users", filters: [['email', '==', this.username]], fill_meta: true })
          .then((result) => {
            const usersData = result.data["users"];
            if (usersData && usersData.length > 0) {
              this.openUserDetails(usersData[0].document_id);
            } else {
              this.createUser();
            }
          })
          .catch((error) => {
            this.showError(error.message);
          });
    },
    createUser() {
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const insertUserFunction = functions.httpsCallable(this.getSubmitEndpoint());
      insertUserFunction({ action: "insert_user", email: this.username })
          .then((result) => {
            const inserted = result.data.payload["users"].inserted;
            if (inserted && inserted.length > 0) {
              this.openUserDetails(inserted[0].document_id);
            }
          })
          .catch((error) => {
            this.showError("Error writing document: " + error);
          });
    },
    openUserDetails(documentId) {
      const path = `/users/${documentId}`;
      if (this.$route.path !== path) {
        this.$router.push({ name: 'UserDetails', params: { id: documentId } });
      }
    }
  },
  mounted() {
    this.loadUserRoleFromServer().then((userRole) => {
      this.userRole = userRole;
    });
    window.addEventListener('keydown', this.onCommandShortcut);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onCommandShortcut);
  },
  created() {
    firebase.auth().onAuthStateChanged(() => {
      const currentUser = firebase.auth().currentUser;
      if (currentUser) {
        ls.set("currentUser", JSON.stringify(currentUser));
      } else {
        ls.remove("currentUser");
      }
      this.isLoggedIn = firebase.auth().currentUser != null && firebase.auth().currentUser.emailVerified;
      this.username = this.isLoggedIn ? firebase.auth().currentUser.email : null;
    });
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/tokens";

html, body {
  background: var(--omx-bg);
  transition: background .18s ease;
}

#app {
  font-family: 'Avenir', 'Inter', system-ui, -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--omx-ink-900);
  min-height: 100vh;
}

#app.has-shell {
  display: block;
  background: var(--omx-bg);
}

.omx-shell__main {
  margin-left: $omx-shell-side-width;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  transition: margin-left .18s ease, background .18s ease;
  background: var(--omx-bg);
}
.side-collapsed .omx-shell__main {
  margin-left: 64px;
}
.omx-shell__content {
  flex: 1;
  padding: 24px 28px;
  min-width: 0;
}

@media (max-width: 900px) {
  .omx-shell__main,
  .side-collapsed .omx-shell__main {
    margin-left: 0;
  }
  .omx-shell__content {
    padding: 16px;
  }
}

/* Kept from previous shell for downstream components that still rely on them */
.menu-icon {
  margin-right: 6px !important;
}
.internal-text {
  color: var(--omx-brand-600);
  font-weight: bold;
}
.external-text {
  color: var(--omx-accent-500);
  font-weight: bold;
}
</style>
